import { SpatialVisionState } from '@states/SpatialVision.js';
import {
  button,
  clear,
  el,
  formatDuration,
  maskIcon,
  statusText,
  vlmServiceTierLabel,
} from './VisionDom.js';

const ICONS = {
  close: '/static/images/icons/X.svg',
  search: '/static/images/icons/search.svg',
  filter: '/static/images/icons/filter.svg',
  direct: '/static/images/icons/zap.svg',
  sequence: '/static/images/icons/sequence.svg',
  completed: '/static/images/icons/circle-check-mark.svg',
  failed: '/static/images/icons/circle-warning.svg',
  canceled: '/static/images/icons/circle-cross.svg',
  running: '/static/images/icons/clock.svg',
  image: '/static/images/icons/image.svg',
  detections: '/static/images/icons/report.svg',
};

const statusKey = status => {
  if (status === 'cancelled') return 'canceled';
  if (status === 'queued' || status === 'pending') return 'running';
  if (status === 'partial') return 'failed';
  return status || 'running';
};

const statusClasses = status => {
  const key = statusKey(status);
  if (key === 'completed') {
    return {
      icon: ICONS.completed,
      className: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    };
  }
  if (key === 'failed') {
    return {
      icon: ICONS.failed,
      className: 'bg-red-50 text-red-500 border-red-100',
    };
  }
  if (key === 'canceled') {
    return {
      icon: ICONS.canceled,
      className: 'bg-red-50 text-red-500 border-red-100',
    };
  }
  return {
    icon: ICONS.running,
    className: 'bg-blue-50 text-blue-600 border-blue-100',
  };
};

const typeTone = type =>
  type === 'sequence'
    ? {
        icon: ICONS.sequence,
        label: 'Sequence run',
        bg: '#F5F3FF',
        color: '#7C3AED',
      }
    : {
        icon: ICONS.direct,
        label: 'Vision chat',
        bg: '#EEF2FF',
        color: '#2563EB',
      };

const jobTime = job =>
  new Date(job.startTime || job.startedAt || job.createdAt || Date.now());

const formatRelativeTime = value => {
  const diff = Date.now() - jobTime(value).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  return `${days}d ago`;
};

const formatDateGroup = job => {
  const date = jobTime(job);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfDate = new Date(date);
  startOfDate.setHours(0, 0, 0, 0);
  const days = Math.floor((startOfToday - startOfDate) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
};

const jobImages = job =>
  (job.imageResults || job.items || []).map((item, index) => ({
    id: item.id || item.imageId || `${job.id}-${index}`,
    url: item.url || item.imageUrl,
    name: item.name || item.filename || `Image ${index + 1}`,
    detectionCount: item.detectionCount ?? item.detections?.length ?? 0,
  }));

const detectionTotal = (job, images = jobImages(job)) =>
  job.detectionCount ??
  images.reduce((sum, image) => sum + Number(image.detectionCount || 0), 0);

export default class VisionHistoryPanel {
  constructor(onClose, onSelectJob = null) {
    this.onClose = onClose;
    this.onSelectJob = onSelectJob;
    this.search = '';
    this.filter = 'all';
    this.dom = el('div', 'absolute inset-0 z-30 hidden');
  }

  update(state, open) {
    clear(this.dom);
    this.dom.classList.toggle('hidden', !open);
    if (!open) return;

    const backdrop = el(
      'div',
      'absolute inset-0 bg-black/20 backdrop-blur-[2px]',
    );
    backdrop.addEventListener('click', this.onClose);
    const panel = el(
      'div',
      'relative ml-auto w-[560px] max-w-full h-full bg-white shadow-[-8px_0_40px_rgba(0,0,0,0.12)] flex flex-col overflow-hidden border-l border-[rgba(0,0,0,0.08)]',
    );
    this.dom.append(backdrop, panel);

    panel.append(this.header(state), this.controls(state), this.list(state));
  }

  header(state) {
    const header = el(
      'div',
      'flex items-center justify-between px-5 pt-5 pb-4 border-b border-[rgba(0,0,0,0.07)]',
    );
    const title = el('div');
    title.append(
      el('h2', 'text-[18.75px] font-semibold text-[#111118]', {
        text: 'Run History',
      }),
      el('p', 'text-[15px] text-[#9999AA] mt-0.5', {
        text: `${state.jobs.length} run${state.jobs.length === 1 ? '' : 's'}`,
      }),
    );
    const close = button(
      'w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F4F8] text-[#8888A0] hover:text-[#333344] transition-colors',
      '',
      this.onClose,
      { title: 'Close history' },
    );
    close.appendChild(maskIcon(ICONS.close, 'w-3.5 h-3.5'));
    header.append(title, close);
    return header;
  }

  controls(state) {
    const controls = el(
      'div',
      'px-4 py-3 space-y-2 border-b border-[rgba(0,0,0,0.06)]',
    );
    const searchWrap = el('div', 'relative');
    const searchIcon = el(
      'span',
      'absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAABC]',
    );
    searchIcon.appendChild(maskIcon(ICONS.search, 'w-3.5 h-3.5'));
    const search = el(
      'input',
      'w-full pl-8 pr-3 py-2 text-[15.625px] bg-[#F4F4F8] rounded-lg outline-none text-[#333344] placeholder:text-[#AAAABC]',
      {
        placeholder: 'Search runs...',
        value: this.search,
      },
    );
    search.addEventListener('input', () => {
      this.search = search.value;
      this.update(SpatialVisionState.spatialVision, true);
    });
    searchWrap.append(searchIcon, search);

    const filteredCount = this.filteredJobs(state).length;
    const filters = el('div', 'flex items-center gap-1.5');
    [
      ['all', 'All runs', ICONS.filter],
      ['direct', 'Direct', ICONS.direct],
      ['sequence', 'Sequence', ICONS.sequence],
    ].forEach(([filter, label, icon]) => {
      const node = button(
        `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[14.375px] font-medium capitalize transition-colors ${
          this.filter === filter
            ? 'bg-[#111118] text-white'
            : 'bg-[#F4F4F8] text-[#666678] hover:bg-[#EEEEF4]'
        }`,
        '',
        () => {
          this.filter = filter;
          this.update(SpatialVisionState.spatialVision, true);
        },
      );
      node.append(
        maskIcon(icon, 'w-2.5 h-2.5'),
        el('span', '', { text: label }),
      );
      filters.appendChild(node);
    });
    filters.appendChild(
      el('span', 'ml-auto text-[13.75px] text-[#AAAABC]', {
        text: `${filteredCount} result${filteredCount === 1 ? '' : 's'}`,
      }),
    );

    controls.append(searchWrap, filters);
    return controls;
  }

  list(state) {
    const list = el('div', 'flex-1 overflow-y-auto');
    const rows = this.filteredJobs(state);
    if (!rows.length) {
      const empty = el(
        'div',
        'h-40 flex flex-col items-center justify-center text-center',
      );
      const icon = el('span', 'text-[#CCCCDA] mb-2');
      icon.appendChild(maskIcon(ICONS.search, 'w-5 h-5'));
      empty.append(
        icon,
        el('p', 'text-[16.25px] text-[#9999AA]', {
          text: 'No runs match your search',
        }),
      );
      list.appendChild(empty);
      return list;
    }

    this.groupJobs(rows).forEach(group => {
      const groupNode = el('div');
      groupNode.appendChild(
        el('div', 'px-5 py-2 bg-[#FAFAFC] border-b border-[rgba(0,0,0,0.05)]', {
          html: `<span class="text-[13.75px] font-semibold text-[#9999AA] uppercase tracking-wider">${group.label}</span>`,
        }),
      );
      group.items.forEach(job => groupNode.appendChild(this.row(job)));
      list.appendChild(groupNode);
    });
    return list;
  }

  filteredJobs(state) {
    return [...(state.jobs || [])]
      .filter(job => {
        if (this.filter !== 'all' && job.type !== this.filter) return false;
        if (
          this.search &&
          !String(job.prompt || '')
            .toLowerCase()
            .includes(this.search.toLowerCase())
        )
          return false;
        return true;
      })
      .sort((a, b) => jobTime(b).getTime() - jobTime(a).getTime());
  }

  groupJobs(jobs) {
    const groups = [];
    jobs.forEach(job => {
      const label = formatDateGroup(job);
      const last = groups[groups.length - 1];
      if (last?.label === label) last.items.push(job);
      else groups.push({ label, items: [job] });
    });
    return groups;
  }

  row(job) {
    const type = typeTone(job.type);
    const images = jobImages(job);
    const detections = detectionTotal(job, images);
    const row = el('div', 'border-b border-[rgba(0,0,0,0.05)]');
    const toggle = button(
      'w-full flex items-start gap-3 px-5 py-4 text-left hover:bg-[#FAFAFD]',
      '',
      () => {
        SpatialVisionState.selectJob(job.id);
        this.onSelectJob?.(job);
        this.onClose();
      },
    );

    const typeBadge = el(
      'div',
      'w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5',
      { style: `background:${type.bg};color:${type.color}` },
    );
    typeBadge.appendChild(maskIcon(type.icon, 'w-3.5 h-3.5'));

    const content = el('div', 'flex-1 min-w-0');
    content.append(
      this.rowMeta(job, type),
      el('p', 'text-[15.625px] text-[#333344] leading-snug mb-2 truncate', {
        text: job.prompt || 'No prompt',
      }),
      this.stats(job, images, detections),
      this.thumbnails(images),
    );

    toggle.append(typeBadge, content);
    row.appendChild(toggle);
    return row;
  }

  rowMeta(job, type) {
    const meta = el('div', 'flex items-center gap-2 mb-1');
    meta.append(
      el('span', 'text-[15px] font-semibold', {
        text: type.label,
        style: `color:${type.color}`,
      }),
      this.statusChip(job.status),
      el('span', 'ml-auto text-[13.75px] text-[#AAAABC] shrink-0', {
        text: formatRelativeTime(job),
      }),
    );
    return meta;
  }

  stats(job, images, detections) {
    const stats = el('div', 'flex items-center gap-3 min-w-0');
    stats.append(
      this.stat(
        ICONS.image,
        `${images.length} image${images.length === 1 ? '' : 's'}`,
      ),
    );
    if (detections > 0) {
      stats.appendChild(
        this.stat(
          ICONS.detections,
          `${detections} detection${detections === 1 ? '' : 's'}`,
        ),
      );
    }
    stats.appendChild(
      this.stat(
        ICONS.running,
        formatDuration(
          job.startTime || job.startedAt,
          job.endTime || job.completedAt,
        ),
      ),
    );
    const provenance = vlmServiceTierLabel(job.serviceTier);
    stats.appendChild(
      el(
        'span',
        'ml-auto max-w-[190px] truncate text-[13.125px] text-[#9999AA]',
        {
          text: provenance,
          title: provenance,
        },
      ),
    );
    return stats;
  }

  stat(icon, text) {
    const node = el(
      'div',
      'flex items-center gap-1 text-[13.75px] text-[#9999AA]',
    );
    node.append(maskIcon(icon, 'w-2.5 h-2.5'), el('span', '', { text }));
    return node;
  }

  thumbnails(images) {
    const wrap = el('div', 'flex items-center gap-1.5 mt-2.5');
    images.slice(0, 4).forEach(image => {
      const thumb = el(
        'div',
        'relative w-12 h-9 rounded-md overflow-hidden bg-[#F0F0F4] shrink-0',
      );
      if (image.url) {
        thumb.appendChild(
          el('img', 'w-full h-full object-cover', {
            src: image.url,
            alt: image.name,
            title: image.name,
          }),
        );
      } else {
        const icon = el(
          'span',
          'w-full h-full flex items-center justify-center text-[#AAAABC]',
        );
        icon.appendChild(maskIcon(ICONS.image, 'w-4 h-4'));
        thumb.appendChild(icon);
      }
      if (image.detectionCount > 0) {
        thumb.appendChild(
          el(
            'div',
            'absolute bottom-0 right-0 bg-black/60 rounded-tl px-1 py-0.5 text-[11.25px] text-white',
            {
              text: String(image.detectionCount),
            },
          ),
        );
      }
      wrap.appendChild(thumb);
    });
    if (images.length > 4) {
      wrap.appendChild(
        el(
          'div',
          'w-12 h-9 rounded-md bg-[#F0F0F4] flex items-center justify-center shrink-0 text-[13.75px] text-[#9999AA]',
          {
            text: `+${images.length - 4}`,
          },
        ),
      );
    }
    return wrap;
  }

  statusChip(status) {
    const variant = statusClasses(status);
    const normalizedStatus = statusKey(status);
    const chip = el(
      'span',
      `inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[12.5px] font-medium border ${variant.className}`,
    );
    chip.append(
      maskIcon(variant.icon, 'w-2.5 h-2.5'),
      el('span', '', { text: statusText(normalizedStatus) }),
    );
    return chip;
  }
}
